// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import Status from 'Ui/components/common/data-table/status';
import {getPurchaseOrders, getPartBins} from 'Services/inventory-service';
import {getInventories} from 'Services/membership-service';
import {fetchEffectData} from 'Utils/react-utils';
import {getFormattedCurrency} from 'Utils/unit-conversion-utils';
import BudgetSidebar from 'Ui/components/common/budget-sidebar';
import OrdersFilterComponent from 'Ui/features/onequip/parts/orders/orders-filter-component';
import {handleDeletePurchaseOrder} from 'Ui/features/onequip/parts/orders/utils/purchase-order-delete';
import moment from 'moment';
import EditLink from 'Ui/components/common/data-table/edit-link';
import {closeDialog, openDialog} from 'Store/actions/dialogs';
import dialogTypes from 'Ui/components/common/dialog-types';
import {connect} from 'react-redux';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {isAuthorized} from 'Common/utils/authorization-handler';
import {VIEW_FINANCIAL_DATA, VIEW_EQUIPMENT_LIST_API, MANAGE_PARTS} from 'Common/constants/business-activities';
import DataGridWrapper from 'Src/ui-core/components/common/data-table/data-grid-wrapper';
import {ColumnHelpers, Box} from '@deere/isg.component-library';
import {DATE_FORMAT_OPTIONS, formatLocalizedTime} from 'Utils/time-utils';

function filterPurchaseHistory(statusFilter, purchaseOrders) {
    return purchaseOrders.filter((orders) => statusFilter.includes(orders.status));
}

function getDataGridColumns(translations, callGetPurchaseOrders, openAddPurchaseOrderDialog, inventories, membership, hasViewEquipmentListApiPerms) {
    return [
        {
            headerName: `${translations.ORDER} #`,
            field: 'referenceNumber',
            width: 150,
            renderCell: (params) => {
                return (
                    hasViewEquipmentListApiPerms &&
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'start',
                            alignItems: 'center',
                            width: '100%',
                            height: '100%'
                        }}
                    >
                        <EditLink
                            onClick={() => {
                                openAddPurchaseOrderDialog({
                                    purchaseOrder: params.row,
                                    inventories,
                                    translations,
                                    onClose: callGetPurchaseOrders,
                                    membership
                                });
                            }}
                        >
                            {params.row.referenceNumber}
                        </EditLink>
                    </Box>
                );
            }
        },
        {
            headerName: translations.ONLINK_INVOICE,
            field: 'invoiceNumber',
            width: 150
        },
        ColumnHelpers.localizedDateColumn({
            headerName: translations.DATE,
            field: 'formattedDateSelectField'
        }, {
            width: 150
        }),
        {
            headerName: translations.DEALER,
            field: 'distributorName',
            flex: 1,
            width: 150
        },
        {
            headerName: translations.ONLINK_TOTAL,
            field: 'totalCost',
            type: 'number',
            width: 150,
            renderCell: (params) => {
                return (
                    params.row.formattedTotalCost
                );
            },
            requiredMyJdPermissions: VIEW_FINANCIAL_DATA
        },
        {
            headerName: translations.STATUS,
            field: 'status',
            width: 150,
            renderCell: (params) => {
                return (
                    <Status
                        className={'orders-status'}
                        formattedStatus={translations[params.row.status]}
                        status={params.row.status}
                    />
                );
            }
        }
    ];
}

function Orders(props) {
    const {
        closeConfirmation,
        translations,
        membership,
        openAddPurchaseOrderDialog,
        openConfirmation,
        myJdPermissions
    } = props;

    const {addToast} = useToast();
    const [purchaseOrders, setPurchaseOrders] = React.useState([]);
    const [budget, setBudget] = React.useState({
        inventory: {},
        partBins: []
    });
    const [inventories, setInventories] = React.useState(null);
    const [statusFilter, setStatusFilter] = React.useState([
        'ordered',
        'completed',
        'pending',
        'received'
    ]);
    const [loading, setLoading] = React.useState(true);

    const userAuth = {
        myJdPermissions,
        isMigrated: membership.isMigrated
    };

    const hasViewFinancialDataPermission = isAuthorized({
        myJdPermissions: VIEW_FINANCIAL_DATA
    }, userAuth);

    const hasViewEquipmentListApiPerms = isAuthorized({
        myJdPermissions: VIEW_EQUIPMENT_LIST_API
    }, userAuth);

    const hasManageParts = isAuthorized({
        myJdPermissions: MANAGE_PARTS
    }, userAuth);

    async function callGetPurchaseOrders(isMounted = () => true) {
        setLoading(true);

        const {inventories} = await getInventories();

        const {partBins} = await getPartBins(inventories[0].inventoryId);

        const {purchaseOrders: purchaseOrdersCall} = await getPurchaseOrders(inventories[0].inventoryId);

        const formattedOrders = purchaseOrdersCall.map((order) => ({
            ...order,
            distributorName: order.distributor?.name,
            formattedDateSelect: formatLocalizedTime(moment(order.dateSelect), DATE_FORMAT_OPTIONS),
            formattedDateSelectField: moment(order.dateSelect, 'YYYYMMDD'),
            formattedTotalCost: getFormattedCurrency(order.totalCost, {
                currencyPreference: membership.currencyPreference,
                maximumFractionDigits: 2
            })
        }));

        if (isMounted()) {
            setPurchaseOrders(formattedOrders);
            setInventories(inventories);
            setBudget({
                inventory: inventories[0],
                partBins
            });
            setLoading(false);
        }
    }

    React.useEffect(() => fetchEffectData(callGetPurchaseOrders), [membership.membershipId]);

    return (
        <DataGridWrapper
            columns={getDataGridColumns(translations, callGetPurchaseOrders, openAddPurchaseOrderDialog, inventories, membership, hasViewEquipmentListApiPerms)}
            deleteHandler={(purchaseOrder) => handleDeletePurchaseOrder(openConfirmation, closeConfirmation, purchaseOrder, callGetPurchaseOrders, translations, addToast)}
            filterComponent={
                <OrdersFilterComponent
                    callGetPurchaseOrders={callGetPurchaseOrders}
                    inventories={inventories}
                    loading={loading}
                    membership={membership}
                    openAddPurchaseOrderDialog={openAddPurchaseOrderDialog}
                    setStatusFilter={setStatusFilter}
                    statusFilter={statusFilter}
                    translations={translations}
                />}
            getRowId={(row) => row.purchaseOrderId}
            initialState={{
                sorting: {
                    sortModel: [{
                        field: 'formattedDateSelectField',
                        sort: 'desc'
                    }, {
                        sort: 'desc',
                        id: 'referenceNumber'
                    }]
                }
            }}
            loading={loading}
            rows={filterPurchaseHistory(statusFilter, purchaseOrders)}
            searchable={true}
            showDeleteColumn={hasManageParts}
            sideComponent={
                hasViewFinancialDataPermission &&
                    <BudgetSidebar
                        budget={budget}
                        currencyPreference={membership.currencyPreference}
                        translations={translations}
                    />
            }
            slotProps={{
                root: {
                    disableColumnMenu: true
                }
            }}
            translations={translations}
        />
    );
}

Orders.propTypes = {
    closeConfirmation: PropTypes.func,
    membership: PropTypes.membership,
    myJdPermissions: PropTypes.myJdPermissions,
    openAddPurchaseOrderDialog: PropTypes.func,
    openConfirmation: PropTypes.func,
    translations: PropTypes.translations
};

export function mapDispatchToProps(dispatch) {
    return {
        closeConfirmation() {
            dispatch(closeDialog(dialogTypes.CONFIRMATION_DIALOG));
        },
        openAddPurchaseOrderDialog(props) {
            dispatch(openDialog(dialogTypes.ADD_EDIT_PURCHASE_ORDER_DIALOG, props));
        },
        openConfirmation(props) {
            dispatch(openDialog(dialogTypes.CONFIRMATION_DIALOG, props));
        }
    };
}
export function mapStateToProps(state) {
    return {
        myJdPermissions: state.account.myJdPermissions
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(Orders);
